import { Assert } from '@cotera/utilities';
import { Suspense, useMemo } from 'react';
import { ModelScope } from '@cotera/client/v0/actions/framework/context';
import { useInScopeModel } from '@cotera/client/v0/actions/framework/use-in-scope-model';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { useTabParams } from '@cotera/client/v0/tab-manager/tab-manager';
import { Text } from '@cotera/client/app/components/ui';
import { useWatchableValue } from '@cotera/client/v0/actions/framework/watchable';
import { TemplatedExpressionEditor } from '@cotera/client/app/components/eraql-editor';
import {
  Button,
  DisplayError,
  Icon,
  Loading,
  Tooltip,
} from '@cotera/client/app/components';
import { useEraScopesAwareRelIR } from '@cotera/client/app/pages/apps/compiler/macro-expansion/scopes';
import { Relation } from '@cotera/era';
import { ColumnModelContext, useColumnModel } from './context';
import { ErrorBoundary } from 'react-error-boundary';
import { cn } from '../../lib/utils';
import { ExpressionColumnModel } from './expression-column.model';
import { useAction } from '../../actions/framework';
import { RunPreviewExpressionColumnAction } from './actions/run-expression-column-preview.action';
import { GraphDataGrid } from '../../data-grid/managed-data-grid';
import { SaveExpressionColumnAction } from './actions/save-expression-column.action';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../../components/ui/resizable';
import { useContainerSize } from '../../components/data-grid/use-container-size';
import { AppTab } from '../../components/tab/tab';
import { useTextFieldTracking } from '@cotera/client/app/etc';

const ColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  const graphModel = useInScopeModel(GraphModel);
  Assert.assert(graphModel !== null);

  const model = useMemo(
    () =>
      new ExpressionColumnModel(
        {
          dataset,
          column,
          graph: graphModel.graph,
        },
        {
          targeted: true,
          isNew,
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset, column]
  );

  return (
    <>
      <AppTab model={model}>
        <ModelScope models={[model]}>
          <ColumnModelContext.Provider value={model}>
            <div className="flex gap-4 h-full w-full py-4">
              <div className="flex flex-col gap-4 w-full h-full">
                <ResizablePanelGroup direction="vertical" className="h-full">
                  <ResizablePanel defaultSize={50} minSize={20}>
                    <EditorSection />
                  </ResizablePanel>

                  <ResizableHandle className="my-2" />

                  <ResizablePanel defaultSize={50} minSize={20}>
                    <div className="pt-1 h-full">
                      <PreviewSection />
                    </div>
                  </ResizablePanel>
                </ResizablePanelGroup>
              </div>
            </div>
          </ColumnModelContext.Provider>
        </ModelScope>
      </AppTab>
    </>
  );
};

const ActionButtonsSection: React.FC = () => {
  const runAction = useAction(RunPreviewExpressionColumnAction);
  const saveAction = useAction(SaveExpressionColumnAction);

  return (
    <div className="flex gap-2">
      <Button
        icon="save"
        theme="secondary"
        compact
        iconOnly
        text={`Save - ${saveAction.action?.shortcut?.join('+')}`}
        tooltip={`top`}
        onClick={() => saveAction.execute()}
      />
      <Button
        icon="filter"
        theme="secondary"
        compact
        label="Apply"
        text={`Preview - ${runAction.action?.shortcut?.join('+')}`}
        tooltip={`top`}
        onClick={() => runAction.execute()}
      />
    </div>
  );
};

const EditorSection: React.FC = () => {
  const model = useColumnModel();
  const { eraQLEditorValue } = useWatchableValue(model.formState);
  const graph = useWatchableValue(model.inputs.graph);
  const trackChange = useTextFieldTracking('EraQL Editor Typed');

  const { containerRef, containerSize } = useContainerSize();

  // Calculate editor height based on container size
  // Reserve space for header (with border) and padding
  const editorHeight = containerSize.height
    ? Math.max(containerSize.height - 80, 100)
    : 200;

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col">
      <div className="flex items-center justify-between pb-4 border-b border-divider">
        <Title className="mb-0">Source EraQL</Title>
        <ActionButtonsSection />
      </div>

      <div className="flex-1 min-h-0 pt-4">
        <TemplatedExpressionEditor
          eraql={eraQLEditorValue}
          templated={false}
          type="expr"
          height={editorHeight}
          parser={graph.parser.forRel(model.datasetName)}
          onChange={(v) => {
            model.formState.set({
              ...model.formState.snapshot(),
              eraQLEditorValue: v,
            });
            trackChange(v);
          }}
        />
      </div>
    </div>
  );
};

const Title: React.FC<{
  title?: string;
  description?: string;
  help?: string;
  className?: string;
  children?: React.ReactNode;
}> = ({ title, description, help, className, children }) => {
  return (
    <div className={cn(className, 'flex flex-row items-center gap-2 mb-2')}>
      <Text.Title subtitle={description} type="section" title={title}>
        {children}
      </Text.Title>
      {help && (
        <Tooltip tooltipContent={help} side="top">
          <div>
            <Icon icon="information-circle" />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export const NewExpressionColumn: React.FC = () => {
  const { id: dataset } = useTabParams<{
    id: string;
  }>();

  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column={'new-column'}
      isNew={true}
    />
  );
};

export const ExpressionColumnEdit: React.FC = () => {
  const { id: dataset, columnId: column } = useTabParams<{
    id: string;
    columnId: string;
  }>();

  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column={column}
      isNew={false}
    />
  );
};

const SuspensefulColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  return (
    <ErrorBoundary
      fallbackRender={(e) => (
        <DisplayError
          error={{
            message: JSON.stringify(e.error),
          }}
        />
      )}
    >
      <Suspense fallback={<Loading.Dots />}>
        <ColumnEditView dataset={dataset} column={column} isNew={isNew} />
      </Suspense>
    </ErrorBoundary>
  );
};

const PreviewSection: React.FC = () => {
  const model = useColumnModel();
  const preview = useWatchableValue(model.preview);

  return (
    <div className="flex flex-col w-full h-full">
      <Title className="mb-2">Preview</Title>
      <div className="flex-1 min-h-0">
        {preview === null && (
          <div>
            <Text.Caption>Run the source EraQL to see a preview</Text.Caption>
          </div>
        )}
        {preview !== null && <PreviewRel />}
      </div>
    </div>
  );
};

const PreviewRel: React.FC = () => {
  const model = useColumnModel();
  const previewRel = useWatchableValue(model.preview)!;
  const relIr = useEraScopesAwareRelIR(previewRel);
  const builtRel = Relation.wrap(relIr);

  return (
    <div className="w-full h-full">
      <GraphDataGrid rel={builtRel} relName={model.id} shouldShow={true} />
    </div>
  );
};
